import React, { useMemo } from 'react';
import './PeFundPopup.css';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';

function fmtEur(value, showSign = false) {
  if (value == null || isNaN(value)) return '—';
  const sign = showSign ? (value >= 0 ? '+' : '−') : '';
  return `${sign}€${Math.round(Math.abs(value)).toLocaleString()}`;
}

function fmtGbp(value) {
  if (value == null || isNaN(value)) return null;
  return `£${Math.round(value).toLocaleString()}`;
}

function fmtUsd(value) {
  if (value == null || isNaN(value)) return null;
  return `$${Math.round(value).toLocaleString()}`;
}

function fmtPct(value, digits = 1) {
  if (value == null || isNaN(value)) return '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}%`;
}

function fmtMult(value) {
  if (value == null || isNaN(value)) return '—';
  return `${Number(value).toFixed(2)}x`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Reusable light popup for PE fund / fund-of-funds LP interests (FOM, EverBlue).
// Driven entirely by investment.peFundDetail; every field is optional so the
// same component renders cleanly whether the fund report is rich or sparse.
function PeFundPopup({ onClose, investment }) {
  const d = investment?.peFundDetail || {};

  const x = useMemo(() => {
    const nav = d.nav_eur ?? investment?.eurValue ?? null;
    const cost = d.cost_basis_eur ?? d.called_eur ?? null;
    const gain = (nav != null && cost != null) ? nav - cost : null;
    const gainPct = (gain != null && cost) ? (gain / cost) * 100 : null;
    return { nav, cost, gain, gainPct };
  }, [d, investment]);

  const title = investment?.name || 'Private equity fund';
  const unverified = d.mark_unverified === true;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content pefund-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="pefund-header">
          <h2>{title}{d.fund_full_name ? ` — ${d.fund_full_name}` : ''}</h2>
          <div className="popup-header-actions">
            <SeeFilesButton url={getDriveFolderUrl(investment?.name)} />
            <button className="pefund-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* KPI row */}
        <div className="pefund-kpis">
          <div className="pefund-kpi">
            <span className="pefund-label">Current value (NAV)</span>
            <span className="pefund-big">{fmtEur(x.nav)}</span>
            <span className="pefund-sub">
              {(fmtGbp(d.nav_gbp) || fmtUsd(d.nav_usd)) ? `${fmtGbp(d.nav_gbp) || fmtUsd(d.nav_usd)} · ` : ''}
              {d.nav_date ? formatDateLong(d.nav_date) : ''}
            </span>
          </div>
          <div className="pefund-kpi">
            <span className="pefund-label">vs paid-in</span>
            <span className={`pefund-mid ${x.gain >= 0 ? 'pefund-pos' : 'pefund-neg'}`}>
              {fmtEur(x.gain, true)}
            </span>
            <span className="pefund-sub">{x.gainPct != null ? `${fmtPct(x.gainPct)} on ${fmtEur(x.cost)}` : ''}</span>
          </div>
          <div className="pefund-kpi">
            <span className="pefund-label">TVPI</span>
            <span className="pefund-mid">{fmtMult(d.tvpi)}</span>
            <span className="pefund-sub">
              {d.dvpi != null ? `DPI ${fmtMult(d.dvpi)}` : ''}
              {d.net_irr_pct != null ? ` · IRR ${fmtPct(d.net_irr_pct)}` : ''}
            </span>
          </div>
        </div>

        {unverified && (
          <div className="pefund-note pefund-note--warn">
            <span className="pefund-note-tag">Unverified</span>
            Mark carried at the last dashboard value — pending the latest fund report.
          </div>
        )}

        {/* Facts */}
        <div className="pefund-facts">
          {d.manager && (
            <div className="pefund-row"><span>Manager</span><strong>{d.manager}</strong></div>
          )}
          {d.strategy && (
            <div className="pefund-row pefund-row--wrap"><span>Strategy</span><strong>{d.strategy}</strong></div>
          )}
          {d.structure && (
            <div className="pefund-row"><span>Structure</span><strong>{d.structure}</strong></div>
          )}
          {d.ownership_pct != null && (
            <div className="pefund-row"><span>Ownership of fund</span><strong>{d.ownership_pct}%</strong></div>
          )}
          {d.commitment_eur != null && (
            <div className="pefund-row">
              <span>Commitment</span>
              <strong>{fmtEur(d.commitment_eur)}{fmtGbp(d.commitment_gbp) ? ` (${fmtGbp(d.commitment_gbp)})` : (fmtUsd(d.commitment_usd) ? ` (${fmtUsd(d.commitment_usd)})` : '')}</strong>
            </div>
          )}
          {d.called_eur != null && (
            <div className="pefund-row">
              <span>Paid-in (called)</span>
              <strong>{fmtEur(d.called_eur)}{fmtUsd(d.called_usd) ? ` (${fmtUsd(d.called_usd)})` : ''}{d.paid_in_pct != null ? ` · ${d.paid_in_pct}%` : ''}</strong>
            </div>
          )}
          {d.uncalled_eur != null && (
            <div className="pefund-row"><span>Unfunded commitment</span><strong>{fmtEur(d.uncalled_eur)}{fmtUsd(d.uncalled_usd) ? ` (${fmtUsd(d.uncalled_usd)})` : ''}</strong></div>
          )}
          {d.distributed_eur != null && (
            <div className="pefund-row"><span>Distributed to date</span><strong>{fmtEur(d.distributed_eur)}</strong></div>
          )}
          {d.nav_source && (
            <div className="pefund-row pefund-row--wrap"><span>NAV source</span><strong>{d.nav_source}</strong></div>
          )}
        </div>

        {d.note && (
          <div className="pefund-note">
            <span className="pefund-note-tag">Note</span> {d.note}
          </div>
        )}

        <div className="pefund-foot">
          {d.source ? `Source: ${d.source}.` : 'Valued at the fund’s reported NAV / partner capital account.'}
          {d.as_of ? ` Last updated ${formatDateLong(d.as_of)}.` : ''}
        </div>
      </div>
    </div>
  );
}

export default PeFundPopup;
